/**
 * Lecture des ressources `billing-*` de l'API intranet (abonnement premium,
 * REDMINE-13522).
 *
 * Volontairement sans vuex-orm : ces ecrans ne font que lire (sauf le
 * catalogue), et rien ne les relie aux modeles de aveine-toolbox. Une ligne
 * est l'objet `attributes` du document JSON:API, avec son `id` et l'id de
 * chaque relation (`<relation>-id`).
 */
const API = process.env.VUE_APP_API_HOST

function flatten (resource) {
  const row = { id: resource.id, ...(resource.attributes || {}) }
  Object.entries(resource.relationships || {}).forEach(([name, relation]) => {
    row[`${name}-id`] = relation && relation.data ? relation.data.id : null
  })
  return row
}

async function request (auth, method, path, body) {
  const token = await auth.getTokenSilently()
  const response = await fetch(`${API}${path}`, {
    method,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/vnd.api+json'
    },
    body: body ? JSON.stringify(body) : undefined
  })
  const text = await response.text()
  const document = text ? JSON.parse(text) : {}
  if (!response.ok) {
    const error = (document.errors && document.errors[0]) || {}
    throw new Error(error.detail || error.title || `HTTP ${response.status}`)
  }
  return document
}

/** Une page de `path` : `{ items, total, included }`. */
export async function search (auth, path, params = {}) {
  const query = new URLSearchParams(params).toString()
  const document = await request(auth, 'GET', `${path}${query ? `?${query}` : ''}`)
  const items = (document.data || []).map(flatten)
  const included = {}
  ;(document.included || []).forEach(resource => { included[`${resource.type}:${resource.id}`] = flatten(resource) })
  const total = document.meta && document.meta.total !== undefined ? document.meta.total : items.length
  return { items, total, included }
}

/** Crée (sans `id`) ou met à jour (avec `id`) un produit du catalogue. */
export async function saveProduct (auth, product) {
  const attributes = {
    store: product.store,
    'product-id': product['product-id'],
    'base-plan-id': product['base-plan-id'] || null,
    // Seule valeur admise pour l'instant ; l'API l'exige (pas de valeur par
    // defaut cote JSON:API).
    plan: product.plan || 'premium',
    period: product.period,
    active: !!product.active
  }
  const data = { type: 'billing-products', attributes }
  if (product.id) {
    data.id = product.id
    return request(auth, 'PATCH', `/billing-products/${product.id}`, { data })
  }
  return request(auth, 'POST', '/billing-products', { data })
}

/**
 * Offre le premium à [userId] (REDMINE-13728) : [expiresAt] en ISO, ou `null`
 * pour sans fin. L'auteur est l'utilisateur de l'intranet, posé par l'API.
 */
export async function offerGrant (auth, userId, reason, expiresAt) {
  return request(auth, 'POST', '/billing-grants', {
    data: {
      type: 'billing-grants',
      attributes: { reason, 'expires-at': expiresAt },
      relationships: { user: { data: { type: 'users', id: userId } } }
    }
  })
}

/** Retire un premium offert, tout de suite. */
export async function revokeGrant (auth, id) {
  return request(auth, 'POST', `/billing-grants/${id}/revoke`)
}

/**
 * Retrouve des comptes pour leur offrir le premium (REDMINE-13728) : par email
 * (exact, chez Auth0), identifiant ou partie du nom de profil. Rend
 * `[{ id, name, email, 'auth-0-id' }]` ; `email` n'est connu que pour une
 * recherche par email.
 */
export async function lookupUsers (auth, query) {
  const document = await request(auth, 'GET', `/user-lookup?${new URLSearchParams({ q: query })}`)
  return document.users || []
}

/** `active`, `upcoming`, `expired` ou `revoked`, à l'instant. */
export function grantState (grant) {
  const now = new Date()
  if (grant['revoked-at']) return 'revoked'
  if (new Date(grant['starts-at']) > now) return 'upcoming'
  if (grant['expires-at'] && new Date(grant['expires-at']) <= now) return 'expired'
  return 'active'
}

/**
 * Le nom de profil de [userId], tiré des ressources incluses par `search`
 * (`<relation>.profile`), sinon le début de son identifiant.
 */
export function profileName (included, userId) {
  if (!userId) return '—'
  const user = included[`users:${userId}`]
  const profileId = user && user['profile-id']
  const profile = profileId && (included[`user_profiles:${profileId}`] || included[`user-profiles:${profileId}`])
  return (profile && profile.name) || userId.slice(0, 8)
}

/** « 3,99 EUR » depuis des millionièmes. */
export function formatPrice (micros, currency) {
  if (micros === null || micros === undefined) return '—'
  return `${(micros / 1000000).toLocaleString(undefined, { minimumFractionDigits: 2 })} ${currency || ''}`.trim()
}

/** Date et heure locales, ou un tiret. */
export function formatDate (value, locale) {
  return value ? new Date(value).toLocaleString(locale) : '—'
}
